import type { EmailPreviewTransaction } from '../../features/variance-tracker/components/variance-supporting-data/utils/email-preview-model.mapper';
import {
  MOCK_EMAIL_GRAPH_SECTIONS,
  mockEmailGraphAssetPath,
} from '../constants/email-mock-graph-sections.constants';
import { cidImageSrc } from '../constants/variance-email.constants';
import {
  emailMockPreviewIframeStyles,
  mockGraphEmailInlineStyles,
} from './email-preview-mock-report.styles';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function resolveAssetUrl(path: string, origin?: string): string {
  const normalized = path.startsWith('/') ? path.slice(1) : path;
  if (origin?.trim()) {
    try {
      return new URL(normalized, `${origin.trim().replace(/\/$/, '')}/`).href;
    } catch {}
  }
  return `/${normalized}`;
}

function readString(model: Record<string, unknown>, key: string): string {
  const v = model[key];
  return typeof v === 'string' ? v : v != null ? String(v) : '';
}

function readBoolean(model: Record<string, unknown>, key: string): boolean {
  const v = model[key];
  return v === true || v === 'true';
}

function readTransactions(model: Record<string, unknown>): EmailPreviewTransaction[] {
  const raw = model['Transactions'];
  if (!Array.isArray(raw)) return [];

  return raw
    .map((row): EmailPreviewTransaction | null => {
      if (!row || typeof row !== 'object') return null;
      const o = row as Record<string, unknown>;
      const cell = (k: string) => {
        const v = o[k];
        return typeof v === 'string' ? v : v != null ? String(v) : '';
      };
      return {
        MedAmt: cell('MedAmt'),
        Uom: cell('Uom'),
        Type: cell('Type'),
        Date: cell('Date'),
        Time: cell('Time'),
        Employee: cell('Employee'),
        EmployeeId: cell('EmployeeId'),
        JobRole: cell('JobRole'),
        ClinicalUnit: cell('ClinicalUnit'),
        VideoLink: cell('VideoLink'),
        AiFlag: cell('AiFlag'),
        RxIntrcpt: cell('RxIntrcpt'),
      };
    })
    .filter((x): x is EmailPreviewTransaction => x !== null);
}

function formatPatientField(deIdentifyPatientData: boolean, value: string, stars: string): string {
  if (deIdentifyPatientData) {
    return escapeHtml(value);
  }
  return stars;
}

function statusIconHtml(show: boolean, src: string): string {
  if (!show || !src.trim()) return '';
  return `<img src="${escapeHtml(src)}" alt="" width="20" height="20" style="vertical-align:middle;margin-right:6px;" />`;
}

function buildMockGraphChartsPreviewBlock(assetOrigin?: string): string {
  const sectionsHtml = MOCK_EMAIL_GRAPH_SECTIONS.map((section, index) => {
    const url = resolveAssetUrl(mockEmailGraphAssetPath(section), assetOrigin);
    const firstClass = index === 0 ? ' mock-graph-section--first' : '';
    return `<div class="mock-graph-section${firstClass}">
            <div class="mock-graph-title">${escapeHtml(section.title)}</div>
            <div class="mock-chart-frame">
                <img src="${escapeHtml(url)}" alt="" />
            </div>
        </div>`;
  }).join('\n');

  return `<div class="mock-analytics-charts">
        ${sectionsHtml}
    </div>`;
}

export function buildMockEmailPreviewHtml(
  model: Record<string, unknown>,
  assetOrigin?: string,
  includeAnalytics = false,
): string {
  const deIdentify = readBoolean(model, 'DeIdentifyPatientData');

  const employeeName = readString(model, 'EmployeeName');
  const employeeId = readString(model, 'EmployeeId');
  const status = readString(model, 'Status');
  const incidentNumber = readString(model, 'IncidentNumber');
  const incidentStatus = readString(model, 'IncidentStatus');
  const patientName = readString(model, 'PatientName');
  const patientId = readString(model, 'PatientId');
  const medicationName = readString(model, 'MedicationName');

  const transactions = readTransactions(model);

  const showStatusIcon = model['ShowStatusIcon'] === true;
  const statusIconSrc = readString(model, 'StatusIconSrc');
  const icon = statusIconHtml(showStatusIcon, statusIconSrc);

  const patientNameDisplay = formatPatientField(deIdentify, patientName, '***** *******');
  const patientIdDisplay = formatPatientField(deIdentify, patientId, '*************');

  const videoCell = (link: string): string => {
    const t = link.trim();
    if (!t) return '';
    return `<a href="${escapeHtml(t)}" class="video-link">Link</a>`;
  };

  const tbody =
    transactions.length === 0
      ? ''
      : transactions
          .map((tx, i) => {
            const rowClass = i % 2 === 0 ? 'row-odd' : 'row-even';
            return `<tr class="${rowClass}">
                        <td>${escapeHtml(tx.MedAmt)}</td>
                        <td>${escapeHtml(tx.Uom)}</td>
                        <td>${escapeHtml(tx.Type)}</td>
                        <td>${escapeHtml(tx.Date)}</td>
                        <td>${escapeHtml(tx.Time)}</td>
                        <td>${escapeHtml(tx.Employee)}</td>
                        <td>${escapeHtml(tx.EmployeeId)}</td>
                        <td>${escapeHtml(tx.JobRole)}</td>
                        <td>${escapeHtml(tx.ClinicalUnit)}</td>
                        <td>${videoCell(tx.VideoLink)}</td>
                        <td class="ai-flag-cell">${escapeHtml(tx.AiFlag)}</td>
                        <td>${escapeHtml(tx.RxIntrcpt)}</td>
                    </tr>`;
          })
          .join('\n');

  const chartsBlock = includeAnalytics ? buildMockGraphChartsPreviewBlock(assetOrigin) : '';

  const pageTitle = `${escapeHtml(medicationName)} - Variance Supporting Data (Incident #${escapeHtml(incidentNumber)})`;

  return `<!DOCTYPE html>
<html lang="en">

<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${pageTitle}</title>
    <style>${emailMockPreviewIframeStyles}
    </style>
</head>

<body>
    <div class="email-body"></div>
    <div class="data-container">
        <div class="header-row">
            <div class="header-block">
                <div class="header-line">Employee: <span class="header-value">${escapeHtml(employeeName)}</span></div>
                <div class="header-line">Employee ID: <span class="header-value">${escapeHtml(employeeId)}</span></div>
                <div class="header-line">Employee Status: <span class="header-value">${icon}${escapeHtml(status)}</span></div>
            </div>
            <div class="header-block">
                <div class="header-line">Incident #: <span class="header-value">${escapeHtml(incidentNumber)}</span></div>
                <div class="header-line">Incident Status: <span class="header-value">${icon}${escapeHtml(incidentStatus)}</span></div>
            </div>
            <div class="header-block">
                <div class="header-line">Patient: <span class="header-value">${patientNameDisplay}</span></div>
                <div class="header-line">Patient ID: <span class="header-value">${patientIdDisplay}</span></div>
            </div>
        </div>

        <div class="title-bar">
            ${escapeHtml(medicationName)} - Variance Supporting Data <span class="incident-ref">(Incident #${escapeHtml(incidentNumber)})</span>
        </div>

        <div class="table-wrap">
            <table>
                <thead>
                    <tr>
                        <th><span class="th-small">Med</span> Amt</th>
                        <th>UOM</th>
                        <th>Type</th>
                        <th>Date</th>
                        <th>Time</th>
                        <th><span class="th-small">Employee</span></th>
                        <th><span class="th-small">Employee</span> ID</th>
                        <th>
                            <p>Job</p>Role
                        </th>
                        <th><span class="th-small">Clinical</span> Unit</th>
                        <th>Video</th>
                        <th>
                            <p>AI</p> Flag
                        </th>
                        <th>
                            <p>RX</p> Intrcpt
                        </th>
                    </tr>
                </thead>
                <tbody>
                    ${tbody}
                </tbody>
            </table>
        </div>
        ${chartsBlock}
    </div>
</body>

</html>`;
}

/**
 * Appended to template `Body` on send when mock graph CIDs are attached.
 * Pass `loadedContentIds` so HTML only references parts that were actually encoded (skipped missing files).
 */
export function buildMockEmailReportChartsBodyFragment(
  loadedContentIds?: ReadonlySet<string>,
): string {
  const sections =
    loadedContentIds === undefined
      ? [...MOCK_EMAIL_GRAPH_SECTIONS]
      : MOCK_EMAIL_GRAPH_SECTIONS.filter((s) => loadedContentIds.has(s.contentId));
  if (sections.length === 0) return '';

  const { title, titleFirst, frame, img } = mockGraphEmailInlineStyles;

  const blocks = sections
    .map((section, index) => {
      const titleStyle = index === 0 ? titleFirst : title;
      const src = cidImageSrc(section.contentId);
      return `<div>
            <div style="${titleStyle}">${escapeHtml(section.title)}</div>
            <div style="${frame}">
                <img src="${src}" alt="" style="${img}" />
            </div>
        </div>`;
    })
    .join('');
  return `<div style="margin-top:8px;">${blocks}</div>`;
}
